/**
 * Register grid — load outcomes and the text behind "Copy details" (AVA-602, showcase B).
 *
 * The grid never renders server text. A failed `table-data` read is classified from the HTTP
 * status and the typed error `code` only; the raw server message goes to the clipboard for support.
 *
 * No Vue / Vuetify / `@/` imports: unit-tested with `node --test`.
 */
import { isRegisterConfigurationRequiredError } from '../../../utils/registerConfigurationError.js';

export const TABLE_LOAD_STATE = Object.freeze({
  OK: 'ok',
  /** Member view of a populated Facility Group register with no Shared/Separated mode. */
  CONFIGURATION_REQUIRED: 'configurationRequired',
  FORBIDDEN: 'forbidden',
  FAILED: 'failed',
});

const HTTP_FORBIDDEN = 403;

function readBody(data) {
  if (data && typeof data === 'object') return data;
  if (typeof data !== 'string' || !data) return null;
  try {
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === 'object' ? parsed : { message: data };
  } catch (_parseErr) {
    return { message: data };
  }
}

/**
 * @param {unknown} err axios error from `getRegisterTableData`
 * @returns {{ state: string, status: number|null, code: string|null, serverMessage: string|null }}
 *   `serverMessage` is for the clipboard only - never render it.
 */
export function classifyTableLoadError(err) {
  const response = err && typeof err === 'object' ? err.response : null;
  const status = Number.isInteger(response?.status) ? response.status : null;
  const body = readBody(response?.data);
  const code = typeof body?.code === 'string' && body.code ? body.code : null;
  const rawMessage = body?.message || body?.error || err?.message || null;
  const serverMessage = typeof rawMessage === 'string' && rawMessage.trim() ? rawMessage.trim() : null;

  let state = TABLE_LOAD_STATE.FAILED;
  if (isRegisterConfigurationRequiredError(err)) state = TABLE_LOAD_STATE.CONFIGURATION_REQUIRED;
  else if (status === HTTP_FORBIDDEN) state = TABLE_LOAD_STATE.FORBIDDEN;
  return { state, status, code, serverMessage };
}

/**
 * Column filter menu ("Select" values, GET column-values): the short note shown under the value
 * picker when the values cannot be read. Classified like a table-data read; server text is never shown.
 */
export const COLUMN_VALUES_NOTE_COPY = Object.freeze({
  [TABLE_LOAD_STATE.FORBIDDEN]: "You don't have access to this column's values. Use Search instead.",
  [TABLE_LOAD_STATE.CONFIGURATION_REQUIRED]: 'Values are unavailable until an administrator sets a Shared or Separated mode.',
  [TABLE_LOAD_STATE.FAILED]: "Couldn't load values for this column. Use Search, or try again.",
});

/**
 * @param {unknown} err axios error from `getColumnFilterValues`
 * @returns {{ state: string, note: string, retryable: boolean }} `retryable` is false when asking
 *   again gives the same answer (no access, register not configured).
 */
export function columnValuesLoadFailure(err) {
  const { state } = classifyTableLoadError(err);
  const known = Object.prototype.hasOwnProperty.call(COLUMN_VALUES_NOTE_COPY, state);
  const resolved = known ? state : TABLE_LOAD_STATE.FAILED;
  return {
    state: resolved,
    note: COLUMN_VALUES_NOTE_COPY[resolved],
    retryable: resolved === TABLE_LOAD_STATE.FAILED,
  };
}

function line(label, value) {
  const text = value === null || value === undefined ? '' : String(value).trim();
  return text ? `${label}: ${text}` : null;
}

/**
 * "Copy register details" (Configuration required): what support needs to find the register.
 * @param {{ registerName?: string, registerId?: string, facilityGroupName?: string,
 *   openedFrom?: string, time?: string }} details
 */
export function registerDetailsClipboardText({
  registerName = '',
  registerId = '',
  facilityGroupName = '',
  openedFrom = '',
  time = '',
} = {}) {
  return [
    line('Register', registerName),
    line('Register ID', registerId),
    line('Facility Group', facilityGroupName),
    line('Opened from', openedFrom),
    'State: rows exist, sharing mode (Shared or Separated) not set',
    line('Time', time),
  ].filter(Boolean).join('\n');
}

/**
 * "Copy details" after a failed load: the only place the raw server message appears.
 * @param {{ registerName?: string, registerId?: string, view?: string, time?: string,
 *   status?: number|null, code?: string|null, serverMessage?: string|null }} details
 */
export function loadFailureClipboardText({
  registerName = '',
  registerId = '',
  view = '',
  time = '',
  status = null,
  code = null,
  serverMessage = null,
} = {}) {
  return [
    line('Register', registerName),
    line('Register ID', registerId),
    line('View', view),
    line('Time', time),
    line('HTTP status', status),
    line('Error code', code),
    line('Server message', serverMessage || 'No response from the server'),
  ].filter(Boolean).join('\n');
}
